// Onglet "Missions AMO" du Catalogue — gestionnaire de VERSIONS nommées du
// Contrat AMO. Lit GET /api/governance/tenant/missions-amo ({versions,default_id}),
// écrit via PUT ({versions}). Le générateur devis/contrat AMO consomme la
// version choisie au moment de la génération (dossier.missions_amo_version).
import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Plus, Trash2, Save, Loader2, FileSignature, Copy, Star } from "lucide-react";
import { toast } from "sonner";

const rid = () => `v-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const newVersion = () => ({
  id: rid(),
  name: "Nouvelle version",
  missions: [{ label: "", ht: 0, ttc: 0, offert: false }],
  mission_ergo: { label: "Ergothérapeute", ht: 0, ttc: 0 },
  aide_deduction: 0,
  aide_deduction_ergo: 0,
  taux_tva: 10,
  is_default: false,
});

export default function MissionsAMOTab({ apiUrl, tenantId, getAuthHeaders }) {
  const [versions, setVersions] = useState([]);
  const [selId, setSelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/governance/tenant/missions-amo`, {
        headers: getAuthHeaders(tenantId), credentials: "include",
      });
      if (!res.ok) throw new Error("Erreur chargement versions missions AMO");
      const d = await res.json();
      const vs = (d.versions || []).map((v) => ({
        ...v,
        missions: (v.missions || []).map((m) => ({ ...m })),
        mission_ergo: { ...(v.mission_ergo || {}) },
      }));
      setVersions(vs);
      setSelId(d.default_id || (vs[0] && vs[0].id) || null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, tenantId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const cur = versions.find((v) => v.id === selId) || null;
  const patchCur = (patch) =>
    setVersions((vs) => vs.map((v) => (v.id === selId ? { ...v, ...patch } : v)));
  const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
  const tvaFactor = () => 1 + (parseFloat(cur?.taux_tva) || 0) / 100;
  // Auto HT↔TTC selon le taux TVA de la version
  const updateMission = (i, field, val) =>
    patchCur({
      missions: cur.missions.map((m, j) => {
        if (j !== i) return m;
        if (field === "ht") return { ...m, ht: val, ttc: r2((parseFloat(val) || 0) * tvaFactor()) };
        if (field === "ttc") return { ...m, ttc: val, ht: r2((parseFloat(val) || 0) / tvaFactor()) };
        return { ...m, [field]: val };
      }),
    });
  const addMission = () =>
    patchCur({ missions: [...cur.missions, { label: "", ht: 0, ttc: 0, offert: false }] });
  const removeMission = (i) =>
    patchCur({ missions: cur.missions.filter((_, j) => j !== i) });
  const setErgo = (field, val) => {
    if (field === "ht") patchCur({ mission_ergo: { ...cur.mission_ergo, ht: val, ttc: r2((parseFloat(val) || 0) * tvaFactor()) } });
    else if (field === "ttc") patchCur({ mission_ergo: { ...cur.mission_ergo, ttc: val, ht: r2((parseFloat(val) || 0) / tvaFactor()) } });
    else patchCur({ mission_ergo: { ...cur.mission_ergo, [field]: val } });
  };
  // Changement du taux TVA → recalcule tous les TTC depuis les HT
  const setTaux = (val) => {
    const f = 1 + (parseFloat(val) || 0) / 100;
    patchCur({
      taux_tva: val,
      missions: cur.missions.map((m) => ({ ...m, ttc: r2((parseFloat(m.ht) || 0) * f) })),
      mission_ergo: { ...cur.mission_ergo, ttc: r2((parseFloat(cur.mission_ergo?.ht) || 0) * f) },
    });
  };
  const totHT = cur ? cur.missions.reduce((s, m) => s + (parseFloat(m.ht) || 0), 0) : 0;
  const totTTC = cur ? cur.missions.reduce((s, m) => s + (parseFloat(m.ttc) || 0), 0) : 0;

  const addVersion = () => { const v = newVersion(); setVersions((vs) => [...vs, v]); setSelId(v.id); };
  const duplicateVersion = () => {
    if (!cur) return;
    const v = { ...cur, id: rid(), name: `${cur.name} (copie)`, is_default: false,
      missions: cur.missions.map((m) => ({ ...m })), mission_ergo: { ...cur.mission_ergo } };
    setVersions((vs) => [...vs, v]); setSelId(v.id);
  };
  const deleteVersion = () => {
    if (versions.length <= 1) { toast.error("Au moins une version requise"); return; }
    setVersions((vs) => {
      const nv = vs.filter((v) => v.id !== selId);
      if (!nv.some((v) => v.is_default)) nv[0].is_default = true;
      setSelId(nv[0].id);
      return nv;
    });
  };
  const setDefault = () =>
    setVersions((vs) => vs.map((v) => ({ ...v, is_default: v.id === selId })));

  const save = async () => {
    if (versions.some((v) => !String(v.name || "").trim())) { toast.error("Chaque version doit avoir un nom"); return; }
    if (versions.some((v) => (v.missions || []).some((m) => !String(m.label || "").trim()))) {
      toast.error("Chaque mission doit avoir un libellé"); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/api/governance/tenant/missions-amo`, {
        method: "PUT", headers: getAuthHeaders(tenantId), credentials: "include",
        body: JSON.stringify({
          versions: versions.map((v) => ({
            id: v.id, name: v.name, is_default: !!v.is_default,
            missions: v.missions.map((m) => ({
              label: m.label, ht: parseFloat(m.ht) || 0, ttc: parseFloat(m.ttc) || 0, offert: !!m.offert,
            })),
            mission_ergo: {
              label: v.mission_ergo?.label || "Ergothérapeute",
              ht: parseFloat(v.mission_ergo?.ht) || 0, ttc: parseFloat(v.mission_ergo?.ttc) || 0,
            },
            aide_deduction: parseFloat(v.aide_deduction) || 0,
            aide_deduction_ergo: parseFloat(v.aide_deduction_ergo) || 0,
            taux_tva: parseFloat(v.taux_tva) || 0,
          })),
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Erreur enregistrement"); }
      toast.success("Versions enregistrées");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[var(--oh-text-secondary)]" /></div>;
  }

  const inputCls = "bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]";

  return (
    <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="missions-amo-tab">
      <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-start md:items-center justify-between">
        <div>
          <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-[var(--oh-info-text)]" /> Missions AMO — versions du Contrat AMO
          </CardTitle>
          <p className="text-xs text-[var(--oh-text-secondary)] mt-1">
            Plusieurs versions nommées (ex. v2 2026, v1 2025). La version est choisie à la génération du devis/contrat.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-missions-amo">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Enregistrer
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Sélecteur de version + actions */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px]">
            <label className="text-xs text-[var(--oh-text-secondary)]">Version éditée</label>
            <Select value={selId || ""} onValueChange={setSelId}>
              <SelectTrigger className={inputCls} data-testid="missions-version-select">
                <SelectValue placeholder="Choisir une version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}{v.is_default ? " ⭐" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={addVersion} className="border-[var(--oh-border)] text-[var(--oh-text)]"><Plus className="h-4 w-4 mr-1" /> Nouvelle</Button>
          <Button variant="outline" onClick={duplicateVersion} disabled={!cur} className="border-[var(--oh-border)] text-[var(--oh-text)]"><Copy className="h-4 w-4 mr-1" /> Dupliquer</Button>
          <Button variant="outline" onClick={setDefault} disabled={!cur || cur.is_default} className="border-[var(--oh-border)] text-[var(--oh-text)]"><Star className="h-4 w-4 mr-1" /> Par défaut</Button>
          <Button variant="outline" onClick={deleteVersion} disabled={versions.length <= 1} className="border-[var(--oh-error-border)] text-[var(--oh-error-text)]"><Trash2 className="h-4 w-4 mr-1" /> Supprimer</Button>
        </div>

        {cur && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <label className="text-xs text-[var(--oh-text-secondary)]">Nom de la version</label>
                <Input value={cur.name} onChange={(e) => patchCur({ name: e.target.value })} className={inputCls} placeholder="ex. AMO Complet v2 (2026)" />
              </div>
              <div>
                <label className="text-xs text-[var(--oh-text-secondary)]">Taux TVA (%)</label>
                <Input type="number" step="0.1" value={cur.taux_tva ?? 10} onChange={(e) => setTaux(e.target.value)} className={`${inputCls} text-right`} data-testid="missions-taux-tva" />
              </div>
              <div>
                {cur.is_default
                  ? <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-[var(--oh-info-border)]">Version par défaut</Badge>
                  : <span className="text-xs text-[var(--oh-text-muted)]">Non par défaut — « Par défaut » pour la pré-sélectionner à la génération.</span>}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-[var(--oh-text)]">Lignes de missions</h4>
                <Button variant="outline" size="sm" onClick={addMission} className="border-[var(--oh-border)] text-[var(--oh-text)]"><Plus className="h-4 w-4 mr-1" /> Mission</Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--oh-border)]">
                    <TableHead className="text-[var(--oh-text-secondary)]">Libellé</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] w-32 text-right">HT (€)</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] w-32 text-right">TTC (€)</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] w-20 text-center">Offert</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cur.missions.map((m, i) => (
                    <TableRow key={i} className="border-[var(--oh-border)]">
                      <TableCell><Input value={m.label} onChange={(e) => updateMission(i, "label", e.target.value)} className={inputCls} placeholder="Libellé de la mission" aria-label="Libellé" /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={m.ht} onChange={(e) => updateMission(i, "ht", e.target.value)} className={`${inputCls} text-right`} aria-label="HT" /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={m.ttc} onChange={(e) => updateMission(i, "ttc", e.target.value)} className={`${inputCls} text-right`} aria-label="TTC" /></TableCell>
                      <TableCell className="text-center"><Checkbox checked={!!m.offert} onCheckedChange={(v) => updateMission(i, "offert", !!v)} className="border-[var(--oh-border)]" aria-label="Offert" /></TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={() => removeMission(i)} aria-label="Supprimer la mission"><Trash2 className="h-4 w-4 text-[var(--oh-error-text)]" /></Button></TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-[var(--oh-border)] bg-[var(--oh-bg-surface)]">
                    <TableCell className="text-[var(--oh-text)] font-medium">Total</TableCell>
                    <TableCell className="text-right text-[var(--oh-text)] font-medium" data-testid="missions-total-ht">{totHT.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-[var(--oh-text)] font-medium" data-testid="missions-total-ttc">{totTTC.toFixed(2)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div className="rounded-lg border border-[var(--oh-border)] bg-[var(--oh-bg-surface)] p-4 space-y-3">
              <h4 className="text-sm font-medium text-[var(--oh-text)]">Mission ergothérapeute (ligne séparée)</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-[var(--oh-text-secondary)]">Libellé</label><Input value={cur.mission_ergo?.label || ""} onChange={(e) => setErgo("label", e.target.value)} className={inputCls} /></div>
                <div><label className="text-xs text-[var(--oh-text-secondary)]">HT (€)</label><Input type="number" step="0.01" value={cur.mission_ergo?.ht ?? 0} onChange={(e) => setErgo("ht", e.target.value)} className={`${inputCls} text-right`} /></div>
                <div><label className="text-xs text-[var(--oh-text-secondary)]">TTC (€)</label><Input type="number" step="0.01" value={cur.mission_ergo?.ttc ?? 0} onChange={(e) => setErgo("ttc", e.target.value)} className={`${inputCls} text-right`} /></div>
              </div>
            </div>

            <div className="rounded-lg border border-[var(--oh-border)] bg-[var(--oh-bg-surface)] p-4 space-y-3">
              <h4 className="text-sm font-medium text-[var(--oh-text)]">Aide MaPrimeAdapt' déduite du forfait AMO</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-[var(--oh-text-secondary)]">Aide déduite — standard (€)</label><Input type="number" step="0.01" value={cur.aide_deduction ?? 0} onChange={(e) => patchCur({ aide_deduction: e.target.value })} className={`${inputCls} text-right`} /></div>
                <div><label className="text-xs text-[var(--oh-text-secondary)]">Aide déduite — avec ergothérapeute (€)</label><Input type="number" step="0.01" value={cur.aide_deduction_ergo ?? 0} onChange={(e) => patchCur({ aide_deduction_ergo: e.target.value })} className={`${inputCls} text-right`} /></div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
